import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEntities, Compare } from "@emberly/zenith-client";
import { ActivityEnums, MissionEnums } from "../../../common/constants";
import moment from "moment/moment";
import SimpleTable from "../../common/tables/SimpleTable";
import { ToggleSurface } from "../../common/core/Surface";
import { MakeEmptyStorage, GetDaysStored, GetStoragePrice } from "../../../common/mission";
import ResponsivePage from "../../common/core/ResponsivePage";
import { useStation } from "../../../providers/StationProvider";
import { MakeActivity } from "../../../common/activity";
import { FullTimeView } from "../../common/core/Time";
import config from "../../../config";

const query = {
  name: "active_storage_filter",
  and: [
    {
      path: "storageTask",
      comparer: Compare.EXISTS,
      value: true
    },
    {
      path: "state",
      comparer: Compare.EQ,
      value: MissionEnums.State.Created
    },
  ]
};

export default function StoragePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { entities, createEntity, loading } = useEntities("Mission", query);
  const { logEvent } = useStation();
  const showLoading = loading && entities.length === 0;

  const onMissionCreate = useCallback(async () => {
    const mission = await createEntity({ storageTask: MakeEmptyStorage() });
    logEvent(MakeActivity(ActivityEnums.Category.Mission, ActivityEnums.Type.MissionDraftCreated, mission?.id, "", "", null, mission?.number));
    navigate(`../drafts/${mission.id}/1`, { relative: "path" });
  }, [createEntity, navigate, logEvent]);

  const onRowClick = useCallback((params) => {
    navigate(`./${params.id}/${params.row.storageTask.number}`, { relative: "path" });
  }, [navigate]);

  const columns = useMemo(() => {
    return [
      {
        field: "number",
        headerName: t("common:tables:mission"),
        flex: 0,
        type: "number",
        maxWidth: 80,
      },
      {
        field: "stored",
        headerName: t("storage:table:stored"),
        type: "dateTime",
        valueGetter: (_, row) => !!row.storageTask?.stored ? new Date(row.storageTask?.stored) : null,
        renderCell: (params) => <FullTimeView time={params.row.storageTask?.stored} isDate />,
        flex: 0.5,
        minWidth: 120,
      },
      {
        field: "object",
        headerName: t("common:tables:object"),
        valueGetter: (_, row) => {
          const type = row.target.type;
          const details = type === 1 ? row.target.carDetails : (type === 2 ? row.target.boatDetails : null);
          return details ? `${details.make} ${details.model}` : "-";
        },
        flex: 1,
        minWidth: 120,
      },
      {
        field: "registration",
        headerName: t("entity:mission:target:registration"),
        valueGetter: (_, row) => {
          const type = row.target.type;
          const details = type === 1 ? row.target.carDetails : (type === 2 ? row.target.boatDetails : null);
          return details?.registration || "-"
        },
        minWidth: 100,
        flex: 1
      },
      {
        field: "warehouse",
        headerName: t("storage:table:warehouse"),
        valueGetter: (_, row) => row.storageTask?.warehouse?.name || "-",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "keyNumber",
        headerName: t("storage:table:keyNumber"),
        valueGetter: (_, row) => row.storageTask?.keyNumber || "-",
        flex: 0.75,
        minWidth: 120,
      },
      {
        field: "reminder",
        headerName: t("storage:table:reminder"),
        type: "dateTime",
        valueGetter: (_, row) => !!row.storageTask?.reminder ? new Date(row.storageTask?.reminder) : null,
        valueFormatter: (value) => !!value ? `${moment(value).format("ll")} (${moment(value).fromNow()})` : "-",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "daysInStorage",
        headerName: t("storage:table:daysInStorage"),
        type: "number",
        valueGetter: (_, row) => GetDaysStored(row.storageTask),
        flex: 0.75,
        minWidth: 120,
      },
      {
        field: "storageCost",
        headerName: t("storage:table:storageCost"),
        type: "number",
        valueGetter: (_, row) => GetStoragePrice(row.storageTask),
        valueFormatter: (value) => `${value},-`,
        flex: 0.75,
        minWidth: 120,
      },
      {
        field: "comment",
        headerName: t("common:comment"),
        valueGetter: (_, row) => row.storageTask?.comment || "-",
        flex: 1,
        minWidth: 120,
      },
    ]
  }, [t]);

  const storedItems = useMemo(() => {
    return entities.filter(t => t.storageTask?.delivered === false);
  }, [entities]);

  return (
    <ResponsivePage
      title={t("storage:title")}
      secondaryAction={t("storage:createStorage")}
      onSecondaryAction={onMissionCreate}
      maxWidth={config.pageSize.large}
    >
      <ToggleSurface xs={12}>
        <SimpleTable
          tableId="storage"
          loading={showLoading}
          rows={storedItems}
          columns={columns}
          onRowClick={onRowClick}
          emptyStateTitle={t("storage:emptyStateTitle")}
          emptyStateDescription={t("storage:emptyStateDescription")}
          sortDescendingByDefault
        />
      </ToggleSurface>
    </ResponsivePage>
  );
}